import { FormControl, TextField, Select, MenuItem, InputLabel, Input, Link, InputAdornment, OutlinedInput, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { alpha, styled } from '@mui/material/styles';

import { visuallyHidden } from '@mui/utils';

import React, { useEffect, useState } from 'react';

import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { AiOutlineSearch } from 'react-icons/ai';

import type { PageInfo } from '@/generated/graphql';

import { ChangePageType } from '@/shared/enums';

import type {
  IPlayerBalanceWithdrawalTable,
  IPlayerBalanceWithdrawalQueryVariable,
  IPlayerWithdrawalTransactionTable_AD,
} from '@/shared/interfaces';

import type { SetStateAction } from 'react';

// components
import { TableBodySkeleton } from '@/components/table/table-body-skeleton';
import { IModalConfirmationDetails, ConfirmationModal } from '@/components/modal/confirmation';

// graphql
import { useAuditorConfirmPlayerWithdrawalMutation, usePendingTransAssignAuditorMutation } from '@/generated/graphql';

type Order = 'asc' | 'desc';

interface TablePageLabel {
  start: number;
  end: number;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IPlayerWithdrawalTransactionTable_AD) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

interface HeadCell {
  id: keyof IPlayerWithdrawalTransactionTable_AD;
  label: string;
  numeric: boolean;
  className: string;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'playerName',
    label: 'Player Name',
    numeric: false,
    className: '',
  },
  {
    id: 'cashierName',
    label: 'Cashier Name',
    numeric: false,
    className: '',
  },
  {
    id: 'receiverAccountNumber',
    label: 'Player account number',
    numeric: false,
    className: '',
  },
  {
    id: 'senderAccountNumber',
    label: 'Sender account number',
    numeric: false,
    className: '',
  },
  {
    id: 'referenceId',
    label: 'Reference ID',
    numeric: false,
    className: '',
  },
  {
    id: 'transactionType',
    label: 'Transaction Type',
    numeric: false,
    className: '',
  },
  {
    id: 'status',
    label: 'Status',
    numeric: false,
    className: '',
  },
  {
    id: 'amountDisplay',
    label: 'Amount',
    numeric: true,
    className: '',
  },
  {
    id: 'createdAt',
    label: 'Created On',
    numeric: false,
    className: '',
  },
];

const StyledTableRow = styled(TableRow)(({}) => ({
  '& .MuiTableCell-root': {
    fontFamily: 'Open Sans,Roboto',
    fontWeight: 600,
    fontSize: 15,
    pt: 15,
    pb: 15,
    pl: 15,
  },
  '& .MuiTableCell-hover	': {
    backgroundColor: '#C2DAF0',
    border: '0.08em solid #C2DAF0',
  },

  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof IPlayerWithdrawalTransactionTable_AD) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ borderBottom: '0.08em solid #ccc', boxShadow: '0em 0.18em 0.4em #bbb' }}>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={index}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: '600', lineHeight: '1.2', color: '#555' }}
            className={headCell.className}
            sx={{ pl: 2, py: 1 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { numSelected, searchHandler } = props;

  const [searchWord, setSearchWord] = useState<string>();

  const searchWordChanges = (value: React.SetStateAction<string | undefined>) => {
    if (value?.length === 0 && searchWord && searchWord.length > 0) {
      searchHandler('');
    }
    setSearchWord(value);
  };

  return (
    <Toolbar
      sx={{
        backgroundColor: '#6591BD',
        color: '#FFFFFF',
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}>
      <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div"></Typography>

      <FormControl sx={{ m: 1, width: '30ch' }} variant="standard">
        <InputLabel htmlFor="standard-adornment-search">Search</InputLabel>
        <Input
          id="standard-adornment-search"
          onChange={(e) => {
            searchWordChanges(e.target.value);
          }}
          onKeyDown={(event) => event.key === 'Enter' && searchHandler(searchWord ? searchWord : '')}
          endAdornment={
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" onClick={(e) => searchHandler(searchWord ? searchWord : '')}>
                <AiOutlineSearch />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </Toolbar>
  );
};
interface lPlayerBalanceWithdrawalsProps {
  rows: readonly IPlayerWithdrawalTransactionTable_AD[];
  setPlayerBalanceWithdrawalQueryVariable: React.Dispatch<SetStateAction<IPlayerBalanceWithdrawalQueryVariable>>;
  playerBalanceWithdrawalQueryVariable: IPlayerBalanceWithdrawalQueryVariable;
  totalCount: number;
  pageInfo: PageInfo | undefined;
  getPlayerBalanceWithdrawalLoading: boolean;
  refetch: () => void;
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  searchHandler: (value: string) => void;
}

export function TablePlayerBalanceWithdrawals(props: lPlayerBalanceWithdrawalsProps) {
  const [auditorConfirmPlayerWithdrawal] = useAuditorConfirmPlayerWithdrawalMutation();
  const [pendingTransAssignAuditor] = usePendingTransAssignAuditorMutation();

  const {
    rows,
    playerBalanceWithdrawalQueryVariable,
    setPlayerBalanceWithdrawalQueryVariable,
    totalCount,
    pageInfo,
    getPlayerBalanceWithdrawalLoading,
    refetch,
  } = props;
  const [search, setSearch] = useState('');

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IPlayerWithdrawalTransactionTable_AD>('createdAt');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [selectedData, setSelectedData] = React.useState<IPlayerWithdrawalTransactionTable_AD | undefined>();
  const [page] = useState(0);
  const [dense] = useState(true);
  const [rowsPerPage] = useState(5);
  const [pageLabel, setPageLabel] = useState<TablePageLabel>({
    start: 1,
    end: playerBalanceWithdrawalQueryVariable.first,
  });

  const [confirmationModal, setConfirmationModal] = useState<IModalConfirmationDetails>({
    isOpen: false,
    title: 'Are you sure ?',
    messages: ['You want to confirm this transaction'],
  });

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IPlayerWithdrawalTransactionTable_AD) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const searchHandler = (value: string) => {
    setSearch(value);

    setPlayerBalanceWithdrawalQueryVariable({
      ...playerBalanceWithdrawalQueryVariable,
      filterBy: {
        ...playerBalanceWithdrawalQueryVariable.filterBy,
        search: value,
      },
    });
  };

  const handleChangePage = (type: ChangePageType) => {
    if (type === ChangePageType.NEXT && pageInfo?.endCursor) {
      // next
      setPlayerBalanceWithdrawalQueryVariable({
        ...playerBalanceWithdrawalQueryVariable,
        after: pageInfo.endCursor,
        before: undefined,
      });
      let end = pageLabel.end + playerBalanceWithdrawalQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start + playerBalanceWithdrawalQueryVariable.first, end });
    } else if (type === ChangePageType.PREV && pageInfo?.startCursor) {
      // prev
      setPlayerBalanceWithdrawalQueryVariable({
        ...playerBalanceWithdrawalQueryVariable,
        before: pageInfo.startCursor,
        after: undefined,
      });
      let end = pageLabel.end - playerBalanceWithdrawalQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start - playerBalanceWithdrawalQueryVariable.first, end });
    }
  };

  const handleChangeRowsPerPage = (value: string | number) => {
    const intValue = parseInt(value.toString(), 10);

    setPlayerBalanceWithdrawalQueryVariable({
      ...playerBalanceWithdrawalQueryVariable,
      first: intValue,
    });
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const transactionAction = (action: 'assign to me' | 'confirm', data: IPlayerWithdrawalTransactionTable_AD) => {
    if (action === 'confirm') {
      setSelectedData(data);
      setConfirmationModal((prev) => ({ ...prev, isOpen: true }));
    } else {
      pendingTransAssignAuditor({
        variables: {
          input: {
            id: data.id,
            type: 'withdrawal',
          },
        },
        onCompleted() {
          setTimeout(() => {
            refetch();
          }, 100);
        },
      });
    }
  };

  const confirmTransaction = () => {
    if (selectedData) {
      auditorConfirmPlayerWithdrawal({
        variables: {
          id: selectedData.id,
        },
        onCompleted() {
          setTimeout(() => {
            setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
            refetch();
          }, 100);
        },
      });
    }
  };

  return (
    <>
      <Box sx={{ width: '100%', mt: 3 }}>
        <Paper
          sx={{
            width: '100%',
            mb: 2,
            backgroundColor: '#ffffff',
          }}>
          <EnhancedTableToolbar numSelected={selected.length} searchHandler={searchHandler} />
          <TableContainer>
            <Table
              sx={{
                minWidth: 850,
              }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              {getPlayerBalanceWithdrawalLoading ? (
                <TableBodySkeleton row={10} column={7} />
              ) : (
                <TableBody>
                  {rows.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <StyledTableRow
                        // hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}>
                        <TableCell align="left">{row.playerName}</TableCell>
                        <TableCell align="left">{row.cashierName}</TableCell>
                        <TableCell align="left">{row.receiverAccountNumber}</TableCell>
                        <TableCell align="left">{row.senderAccountNumber}</TableCell>
                        <TableCell align="left">{row.referenceId}</TableCell>

                        <TableCell align="left">{row.transactionType}</TableCell>
                        <TableCell align="left">{row.status}</TableCell>
                        <TableCell align="right">{row.amountDisplay}</TableCell>
                        <TableCell align="left">{row.createdAt}</TableCell>
                        <TableCell align="left">
                          {row.auditorId.length <= 0 ? (
                            <Button
                              onClick={() => transactionAction('assign to me', row)}
                              variant="contained"
                              size="small"
                              sx={{ textTransform: 'unset', fontSize: '10px' }}>
                              Assign to me
                            </Button>
                          ) : (
                            <Button
                              onClick={() => transactionAction('confirm', row)}
                              variant="contained"
                              color="success"
                              size="small"
                              sx={{ textTransform: 'unset', fontSize: '10px' }}>
                              Confirm
                            </Button>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <Box
            sx={{
              backgroundColor: '#6591BD',
              color: '#FFFFFF',
              mt: 1,
              justifyContent: 'end',
              alignItems: 'center',
              display: 'flex',
            }}>
            <Typography variant="body2" mr={4} gutterBottom>
              Rows per page:
            </Typography>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 50 }}>
              <Select
                onChange={(e) => {
                  handleChangeRowsPerPage(e.target.value);
                }}
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={playerBalanceWithdrawalQueryVariable.first}
                label="Row">
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" mx={4} gutterBottom>
              {pageLabel.start} - {pageLabel.end} of {totalCount}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(ChangePageType.PREV)}
              disabled={pageInfo?.hasPreviousPage ? false : true}
              size={'small'}
              sx={{ mr: 2 }}
              aria-label="prev">
              <IoIosArrowBack />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(ChangePageType.NEXT)}
              disabled={pageInfo?.hasNextPage ? false : true}
              size={'small'}
              aria-label="next">
              <IoIosArrowForward />
            </IconButton>
          </Box>
        </Paper>
      </Box>
      <ConfirmationModal
        modalDetails={confirmationModal}
        setModalDetails={setConfirmationModal}
        confirm={confirmTransaction}
        confirmText={'confirm'}
      />
    </>
  );
}
